import { useCallback, useEffect, useMemo } from "react";
import { Dimmer, Segment } from "semantic-ui-react";
import FormFooter from "../../../../components/forms/FormFooter";
import { type ParentAccount, type AccountConfigurationInfo } from "../../types";
import { AccountConfiguration } from "../../Configuration/AccountConfiguration";
import { useFormBinding } from "hooks/useFormBinding";
import cn from "classnames";
import { connect, type ConnectedProps } from "react-redux";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { fetchAccountTypes as fetchAccountTypesAction } from "../state/editAccountActions";
import { fetchContractTypes as fetchContractTypesAction } from "features/Accounts/state/thunks/contractTypesThunk";
import { fetchParentAccounts as fetchParentAccountsAction } from "features/Accounts/state/thunks/parentAccountThunk";
import { setNewTag as setNewTagAction } from "features/Accounts/state/slices/tagsSlice";

import styles from "./EditAccountConfiguration.module.scss";

export interface EditInfoOwnProps {
  accountId: number;
  isOwnAccount: boolean;
  isLoading: boolean;
  onInfoUpdated(info: AccountConfigurationInfo, accountId: number, isReadOnly: boolean): void;
}

const EditAccountConfiguration = ({
  accountId,
  isOwnAccount,
  fetchAccountTypes,
  accountTypes,
  fetchContractTypes,
  contractTypes,
  isLoading,
  parentAccounts,
  onInfoUpdated,
  isMobo,
  hasGlobalAccess,
  fetchParentAccounts,
  accountsLabels,
  setNewTag,
  accountInfo,
}: EditInfoOwnProps & PropsFromRedux) => {
  const { isDirty, isValid, setIsDirty, setIsValid, formRef, handleSubmit, handleReset } = useFormBinding();

  const configuration = useMemo(() => {
    return { ...accountInfo, contractTypeId: accountInfo.contractTypeId ?? -1 };
  }, [accountInfo]);

  const { parentAccountId, parentName } = configuration;

  const parents = useMemo(() => {
    const currentParent = {
      id: parentAccountId,
      name: parentName,
    };

    if (!hasGlobalAccess || isOwnAccount) {
      return currentParent.id
        ? {
            items: [currentParent as ParentAccount],
            isLoading: false,
          }
        : { items: [], isLoading: false };
    }

    return parentAccounts;
  }, [parentAccountId, parentName, hasGlobalAccess, isOwnAccount, parentAccounts]);

  const accountTypesInternal = useMemo(() => {
    const { accountTypeId, accountTypeName } = configuration;

    return isOwnAccount
      ? [
          {
            id: accountTypeId,
            name: accountTypeName,
          },
        ]
      : accountTypes;
  }, [isOwnAccount, accountTypes, configuration]);

  useEffect(() => {
    if (!isOwnAccount) {
      fetchAccountTypes(accountId);
    }
  }, [accountId, fetchAccountTypes, isOwnAccount]);

  useEffect(() => {
    fetchContractTypes();
  }, [fetchContractTypes]);

  const onSubmit = (newAccountInfo: AccountConfigurationInfo) => {
    const accountInfo = {
      ...newAccountInfo,
      contractTypeId: newAccountInfo.contractTypeId === -1 ? null : newAccountInfo.contractTypeId,
      alias: !newAccountInfo.alias ? undefined : newAccountInfo.alias,
      timeZone: newAccountInfo.timeZone,
    };
    onInfoUpdated(accountInfo, accountId, isOwnAccount);
  };

  const handleFetchParents = useCallback(
    (typeId: number) => {
      return fetchParentAccounts(typeId, accountId);
    },
    [fetchParentAccounts, accountId],
  );

  return (
    <>
      <Dimmer.Dimmable as={Segment} dimmed={isLoading} className={cn(styles["container"], "scrollable-content")}>
        <Dimmer active={isLoading} inverted />
        <div className={cn("stretch scrollable-content", "edit-form", styles["account-edit-info"])}>
          <AccountConfiguration
            ref={formRef}
            accountId={accountId}
            isOwnAccount={isOwnAccount}
            currentConfiguration={configuration}
            onIsValidChange={setIsValid}
            onDirtyChanged={setIsDirty}
            onSubmit={onSubmit}
            accountTypes={accountTypesInternal}
            parentAccounts={parents.items}
            contractTypes={contractTypes}
            isMobo={isMobo}
            hasGlobalAccess={hasGlobalAccess}
            isEdit
            showAccountId
            onFetchParentAccounts={handleFetchParents}
            isLoadingParentAccounts={parents.isLoading}
            accountsLabels={accountsLabels}
            onAddNewTag={setNewTag}
          />
        </div>
      </Dimmer.Dimmable>
      <FormFooter
        isSaveBtnDisabled={!(isDirty && isValid)}
        isCancelBtnDisabled={!isDirty}
        onSave={handleSubmit}
        onCancel={handleReset}
      />
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { edit, contractTypes } = state.accounts;
  const { userProfile } = state;

  return {
    accountTypes: edit.accountInfo.accountTypes,
    contractTypes: contractTypes.items,
    accountInfo: edit.accountInfo.accountConfiguration,
    isMobo: userProfile.isMobo,
    hasGlobalAccess: userProfile.hasGlobalAccess,
    parentAccounts: state.accounts.parentAccounts,
    accountsLabels: state.accounts.tags.items,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    fetchAccountTypes: bindAction(fetchAccountTypesAction, dispatch),
    fetchContractTypes: bindAction(fetchContractTypesAction, dispatch),
    fetchParentAccounts: bindAction(fetchParentAccountsAction, dispatch),
    setNewTag: bindAction(setNewTagAction, dispatch),
  };
};

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditAccountConfiguration);
export default ConnectedComponent;
